import React, { useEffect, useMemo, useRef } from 'react'
import * as yup from 'yup'

import {
  DatePicker,
  Form,
  FormButtons,
  OptionSelector,
  TextInput,
} from '@/components'
import CarSubModelSelector from '@/pages/Car/carSubModelSelector'
import DriverSelector from '@/pages/Drivers/driverSelector'
import { CarCarStatusChoices, CarNode } from '@/apollo/graphql'
import { useApp, useForm } from '@/hooks'
import { getMaskedString } from '@/utils'
import { ICar } from '@/types'
import { t } from '@/i18n'

import { useBankSelect } from './useBankSelect'
import { useSelect } from '../../Car/useSelect'
import { buildData } from '../utils'
import { Link, useNavigate } from 'react-router-dom'

interface Props {
  loading: boolean
  isEditing?: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: ICar) => void
  initialData?: Partial<CarNode>
}

const CarForm: React.FC<Props> = ({
  loading,
  onCancel,
  onSubmit,
  isEditing,
  submitting,
  initialData,
}) => {
  const navigate = useNavigate()
  const { isRTL } = useApp()
  const schema: any = useMemo(
    () =>
      yup
        .object({
          creationYear: yup.string().required(t('required_field_error')),
          number: yup
            .string()
            .min(9, t('invalid_cformat_error'))
            .max(10, t('invalid_cformat_error'))
            .matches(
              /^\(?([0-9]{2})\)?-?([0-9]{3})-?([0-9]{2})$|^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{3})$/,
              t('invalid_cformat_error'),
            )
            .required(t('required_field_error')),
          status: yup
            .mixed<CarCarStatusChoices>()
            .oneOf(
              Object.values(CarCarStatusChoices),
              t('required_field_error'),
            ),
          carSubModel: yup
            .string()
            .nullable()
            .required(t('required_field_error')),
        })
        .required(),
    [],
  )

  const { carStatusOptions, carServiceOptions } = useSelect()

  const {
    loadingBanks,
    bankOptions,
    banksPageInfo,
    onFetchMore: onFetchBanksMore,
  } = useBankSelect({
    loading,
    initialBank: initialData?.bank,
  })

  const { control, isValid, onTriggerChange, onFormSubmit, getValues } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const formRef = useRef<HTMLFormElement | null>(null)

  useEffect(() => {
    if (initialData?.number) {
      onTriggerChange('number', getMaskedString(initialData.number, true))
    }
  }, [onTriggerChange, initialData?.number])

  useEffect(() => {
    if (initialData?.bank) {
      onTriggerChange('bank', initialData.bank.id)
    }
  }, [initialData?.bank, onTriggerChange])

  useEffect(() => {
    if (initialData?.driver) {
      onTriggerChange('driver', initialData.driver.id)
    }
  }, [initialData?.driver, onTriggerChange])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  const details = useMemo(
    () => (initialData ? buildData(initialData, isRTL).general_info : []),
    [initialData, isRTL],
  )

  return (
    <div className="content-box">
      <div className="scroll-wrap">
        <div className="content-title">
          <h2>{initialData?.number}</h2>
          {isEditing ? (
            <Link to={'/cars/' + initialData?.id}>{t('full_edit')}</Link>
          ) : null}
        </div>
        <div className="edit-info">
          <Form<ICar>
            formRef={formRef}
            control={control}
            isEditing={isEditing}
            onSubmit={onFormSubmit}
          >
            <div className="form-fields">
              {details.map(field => {
                if (!field.name || field.editable === false) return null
                return (
                  <TextInput
                    key={field.name}
                    control={control}
                    name={field.name}
                    label={t(field.label)}
                    emptyValue={emptyValue}
                    className={field.className}
                    disabled={!Boolean(isEditing)}
                    {...field.props}
                  />
                )
              })}
              <OptionSelector
                name="serviceProvider"
                className="w-50"
                menuPlacement="top"
                control={control}
                options={carServiceOptions}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('serviceProvider')}
                // @ts-ignore
                value={initialData?.serviceProvider}
                fieldValue={initialData?.serviceProvider}
              />
              <OptionSelector
                name="bank"
                className="w-50"
                label={t('bank')}
                control={control}
                menuPlacement="top"
                options={bankOptions}
                emptyValue={emptyValue}
                optionsLoading={loadingBanks}
                disabled={!Boolean(isEditing)}
                onFetchMore={() => onFetchBanksMore()}
                fetchMoreStatus={banksPageInfo.fetchMoreStatus}
              />
              <OptionSelector
                required
                name="status"
                className="w-50"
                control={control}
                label={t('status')}
                menuPlacement="top"
                emptyValue={emptyValue}
                options={carStatusOptions}
                disabled={!Boolean(isEditing)}
                defaultValue={CarCarStatusChoices.WaitingForSale}
              />
              <CarSubModelSelector
                isFormElement
                className="w-50"
                control={control}
                loading={loading}
                isEditing={isEditing}
                onTriggerChange={onTriggerChange}
                initialSubModel={initialData?.carSubModel}
                initialModel={initialData?.carSubModel?.carModel}
                initialBrand={initialData?.carSubModel?.carModel?.brand}
              />
              <DriverSelector
                creatable
                onCreateOption={() =>
                  navigate(`/cars/${initialData?.id}/drivers/add`)
                }
                isFormElement
                className="w-50"
                control={control}
                isEditing={isEditing}
                initial={initialData?.driver}
                selected={getValues('driver')}
              />
              <DatePicker
                control={control}
                className="w-50"
                name="licenceExpDate"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('license_exp_date')}
              />
              <TextInput
                required
                control={control}
                name="creationYear"
                type="number"
                min={0}
                className="w-50"
                isEditing={isEditing}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('creation_year')}
              />
              <DatePicker
                control={control}
                className="w-50"
                name="insuranceExpDate"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('insurance_exp_date')}
              />
              <TextInput
                control={control}
                className="w-50"
                isEditing={isEditing}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                name="vehicleCode"
                label={t('vehicleCode')}
              />
            </div>
          </Form>
          <FormButtons
            formRef={formRef}
            visible={isEditing}
            onCancel={onCancel}
            isFormValid={isValid}
            submitting={submitting}
            cancelClassName={'gray'}
          />
        </div>
      </div>
    </div>
  )
}

export default CarForm
